import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Plus, Workflow, Zap, Clock, Trash2, Edit2,
    ShieldAlert, RefreshCw, ChevronRight,
    LayoutTemplate, Share2, Play
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/api';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter
} from "./ui/sheet";

const TRIGGER_LABELS = {
    REPLY: 'Reply Received',
    HARD_BOUNCE: 'Hard Bounce',
    SOFT_BOUNCE: 'Soft Bounce',
    AUTO_REPLY: 'Auto-Reply',
    OUT_OF_OFFICE: 'Out of Office',
    NO_REPLY: 'No Reply After Delay'
};

const ACTION_LABELS = {
    TASK: 'Create Task',
    STATUS_CHANGE: 'Update Contact Status',
    SUPPRESS: 'Add to Blocklist',
    REPLY: 'Send Auto-Reply (Legacy)'
};

export default function InboxAutomations() {
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const [rules, setRules] = useState([]);
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [newFlowName, setNewFlowName] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, type: 'VISUAL' | 'LEGACY' }
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        name: '',
        trigger: { type: 'CLASSIFICATION', value: 'REPLY', delayHours: 24 },
        actions: [{ type: 'TASK', params: {} }],
        isActive: true
    });

    const [activeTab, setActiveTab] = useState('VISUAL'); // 'VISUAL' or 'LEGACY'

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [rulesRes, automationsRes] = await Promise.all([
                api.get('/api/inbound-rules'),
                api.get('/api/inbound-automations')
            ]);
            setRules(rulesRes.data.data || []);
            setAutomations(automationsRes.data.data || []);
        } catch (err) {
            showSnackbar(err.message || 'Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreateFlowClick = () => {
        setNewFlowName('');
        setIsCreateModalOpen(true);
    };

    const submitCreateFlow = async (e) => {
        e.preventDefault();
        const trimmedName = newFlowName.trim();
        if (!trimmedName) return;

        setIsCreating(true);
        try {
            const res = await api.post('/api/inbound-automations', { name: trimmedName });
            setIsCreateModalOpen(false);
            navigate(`/inbox/automations/${res.data.data._id}/builder`);
        } catch (err) {
            showSnackbar(err.message || 'Failed to create flow', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteClick = (id, type) => {
        setDeleteTarget({ id, type });
        setIsDeleteModalOpen(true);
    };

    const submitDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            if (deleteTarget.type === 'VISUAL') {
                await api.delete(`/api/inbound-automations/${deleteTarget.id}`);
            } else {
                await api.delete(`/api/inbound-rules/${deleteTarget.id}`);
            }
            showSnackbar('Automation deleted', 'success');
            setIsDeleteModalOpen(false);
            fetchData();
        } catch (err) {
            showSnackbar(err.message || 'Delete failed', 'error');
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/api/inbound-rules/${editingId}`, form);
                showSnackbar('Rule updated successfully', 'success');
            } else {
                await api.post('/api/inbound-rules', form);
                showSnackbar('Rule created successfully', 'success');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            showSnackbar(err.message || 'Operation failed', 'error');
        }
    };

    const openCreateModal = () => {
        setIsEditing(false);
        setEditingId(null);
        setForm({
            name: '',
            trigger: { type: 'CLASSIFICATION', value: 'REPLY', delayHours: 24 },
            actions: [{ type: 'TASK', params: {} }],
            isActive: true
        });
        setIsModalOpen(true);
    };

    const openEditModal = (rule) => {
        setIsEditing(true);
        setEditingId(rule._id);
        setForm({
            name: rule.name,
            trigger: rule.trigger,
            actions: rule.actions,
            isActive: rule.isActive
        });
        setIsModalOpen(true);
    };

    return (
        <div className="flex flex-col h-full p-6 space-y-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
                        <Workflow className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-foreground">Inbox Automations</h1>
                        <p className="text-sm text-muted-foreground">Automate your inbound workflow with visual flows or legacy rules.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={openCreateModal} className="h-10 text-muted-foreground hover:text-foreground">
                        Legacy Rule (Compatibility)
                    </Button>
                    <Button onClick={handleCreateFlowClick} className="gap-2 shrink-0 h-10 px-6 font-semibold shadow-lg shadow-primary/20">
                        <Plus className="h-4 w-4" /> New Visual Flow
                    </Button>
                </div>
            </div>

            {/* Transition Alert */}
            <div className="bg-primary/5 border border-primary/10 rounded-2xl p-4 flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Zap className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h4 className="text-sm font-bold text-primary italic">Product Update: Visual flows are our new standard for multi-step automations.</h4>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Visual flows offer advanced branching and field-specific conditions. Your existing legacy rules continue to work and are perfect for simple trigger-action logic.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
                <button
                    className={`px-6 py-2 text-sm font-bold transition-all border-b-2 ${activeTab === 'VISUAL' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('VISUAL')}
                >
                    Visual Flows ({automations.length})
                </button>
                <button
                    className={`px-6 py-2 text-sm font-bold transition-all border-b-2 ${activeTab === 'LEGACY' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('LEGACY')}
                >
                    Legacy Rules (Compatibility)
                </button>
            </div>

            <div className="grid gap-6">
                {loading ? (
                    <div className="h-64 flex items-center justify-center border border-dashed rounded-2xl">
                        <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin opacity-20" />
                    </div>
                ) : activeTab === 'VISUAL' ? (
                    automations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-16 border-2 border-dashed rounded-3xl bg-muted/5">
                            <Share2 className="h-12 w-12 text-muted-foreground/30 mb-4" />
                            <h3 className="text-lg font-semibold text-foreground">No visual flows yet</h3>
                            <p className="text-sm text-muted-foreground mt-1 text-center max-w-xs">
                                Use the visual builder to create complex trigger-condition-action logic.
                            </p>
                            <Button variant="outline" className="mt-6 font-semibold" onClick={handleCreateFlowClick}>Start Building</Button>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {automations.map(auto => (
                                <div
                                    key={auto._id}
                                    className="group relative bg-card border rounded-2xl p-5 hover:shadow-lg hover:border-primary/30 transition-all flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${auto.status === 'PUBLISHED' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                            <LayoutTemplate className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-bold text-lg text-foreground">{auto.name}</h3>
                                                <Badge variant={auto.status === 'PUBLISHED' ? 'default' : 'secondary'} className="text-[10px] uppercase">
                                                    {auto.status}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1"><Play className="h-3 w-3" /> {auto.stats?.totalRuns || 0} Runs</span>
                                                <span>•</span>
                                                <span>Last edited {new Date(auto.updatedAt).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" className="font-semibold" onClick={() => navigate(`/inbox/automations/${auto._id}/builder`)}>
                                            Open Builder
                                        </Button>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteClick(auto._id, 'VISUAL')}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : rules.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-16 border-2 border-dashed rounded-3xl bg-muted/5">
                        <Zap className="h-12 w-12 text-muted-foreground/30 mb-4" />
                        <h3 className="text-lg font-semibold text-foreground">No automations yet</h3>
                        <p className="text-sm text-muted-foreground mt-1 text-center max-w-xs">
                            Create rules to automatically handle replies, bounces, or follow up with silent leads.
                        </p>
                        <Button variant="outline" className="mt-6" onClick={openCreateModal}>Create your first rule</Button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {rules.map(rule => (
                            <div
                                key={rule._id}
                                className={`group relative bg-card border rounded-2xl p-5 hover:shadow-lg hover:border-primary/30 transition-all ${!rule.isActive && 'opacity-60'}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${rule.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                            {rule.trigger.type === 'CLASSIFICATION' ? <Zap className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-bold text-lg text-foreground">{rule.name}</h3>
                                                {!rule.isActive && <Badge variant="secondary" className="text-[10px] uppercase">Inactive</Badge>}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <span>When {TRIGGER_LABELS[rule.trigger.value] || rule.trigger.type}</span>
                                                <ChevronRight className="h-3 w-3" />
                                                <span className="text-foreground font-medium">{rule.actions.map(a => ACTION_LABELS[a.type]).join(', ')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="ghost" size="icon" onClick={() => openEditModal(rule)}>
                                            <Edit2 className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteClick(rule._id, 'LEGACY')}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                {rule.lastTriggeredAt && (
                                    <div className="mt-4 pt-4 border-t flex items-center gap-2 text-[11px] text-muted-foreground">
                                        <RefreshCw className="h-3 w-3" />
                                        Last triggered {new Date(rule.lastTriggeredAt).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Rule Modal */}
            <Sheet open={isModalOpen} onOpenChange={setIsModalOpen}>
                <SheetContent side="right" className="sm:max-w-[500px]">
                    <SheetHeader>
                        <SheetTitle>{isEditing ? 'Edit Automation Rule' : 'Create Automation Rule'}</SheetTitle>
                        <SheetDescription className="text-[11px] leading-relaxed">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-black border border-amber-500/20 uppercase tracking-tighter mb-2">
                                <ShieldAlert className="h-3 w-3" /> Compatibility Mode
                            </span>
                            <br />
                            Legacy rules are restricted to simple trigger-action logic. For advanced branching, richer email authoring, and multi-condition paths, use <button type="button" onClick={() => { setIsModalOpen(false); handleCreateFlowClick(); }} className="text-primary font-black underline hover:text-primary/80">Visual Flows</button>.
                        </SheetDescription>
                    </SheetHeader>

                    <form onSubmit={handleSave} className="space-y-6 pt-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rule Name</label>
                            <input
                                required
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g., Auto-create task for replies"
                                className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                            />
                        </div>

                        <div className="grid gap-4 p-4 rounded-xl bg-muted/30 border border-border">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Trigger</label>
                                <select
                                    className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                                    value={form.trigger.type === 'CLASSIFICATION' ? form.trigger.value : 'NO_REPLY'}
                                    onChange={e => {
                                        const val = e.target.value;
                                        if (val === 'NO_REPLY') {
                                            setForm({ ...form, trigger: { ...form.trigger, type: 'NO_REPLY', value: 'NO_REPLY' } });
                                        } else {
                                            setForm({ ...form, trigger: { ...form.trigger, type: 'CLASSIFICATION', value: val } });
                                        }
                                    }}
                                >
                                    <option value="REPLY">On Reply Received</option>
                                    <option value="AUTO_REPLY">On Auto-Reply / OOO</option>
                                    <option value="HARD_BOUNCE">On Hard Bounce</option>
                                    <option value="NO_REPLY">If No Reply Received (Timer)</option>
                                </select>
                                <p className="text-[10px] text-muted-foreground/60 italic">Heuristic-based classification is applied to inbound signals.</p>
                            </div>

                            {form.trigger.type === 'NO_REPLY' && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Delay (Hours)</label>
                                    <input
                                        type="number"
                                        value={form.trigger.delayHours}
                                        onChange={e => setForm({ ...form, trigger: { ...form.trigger, delayHours: parseInt(e.target.value) } })}
                                        className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                                    />
                                    <p className="text-[10px] text-muted-foreground">The rule will trigger if no activity is found X hours after the email was sent.</p>
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Action</label>
                            <select
                                className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                                value={form.actions[0].type}
                                onChange={e => {
                                    const newActions = [...form.actions];
                                    newActions[0].type = e.target.value;
                                    setForm({ ...form, actions: newActions });
                                }}
                            >
                                <option value="TASK">Create a Task</option>
                                <option value="STATUS_CHANGE">Update Contact Status</option>
                                <option value="REPLY">Send Auto-Reply (Legacy)</option>
                                <option value="SUPPRESS">Add to Blocklist</option>
                            </select>
                            <p className="text-[10px] text-muted-foreground/60">Legacy actions are atomic and non-branching.</p>
                        </div>

                        {form.actions[0].type === 'STATUS_CHANGE' && (
                            <div className="space-y-4 p-4 rounded-xl bg-muted/20 border border-border/50 animate-in slide-in-from-top-2 duration-200">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">New Lifecycle Status</label>
                                    <select
                                        className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                                        value={form.actions[0].params.status || 'Replied'}
                                        onChange={e => {
                                            const newActions = [...form.actions];
                                            newActions[0].params = { ...newActions[0].params, status: e.target.value };
                                            setForm({ ...form, actions: newActions });
                                        }}
                                    >
                                        <option value="LEAD">Lead</option>
                                        <option value="Replied">Replied</option>
                                        <option value="Interested">Interested</option>
                                        <option value="Bounced">Bounced</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {form.actions[0].type === 'REPLY' && (
                            <div className="space-y-4 p-4 rounded-xl bg-muted/20 border border-border/50 animate-in slide-in-from-top-2 duration-200">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Email Subject</label>
                                    <input
                                        value={form.actions[0].params.subject || ''}
                                        onChange={e => {
                                            const newActions = [...form.actions];
                                            newActions[0].params = { ...newActions[0].params, subject: e.target.value };
                                            setForm({ ...form, actions: newActions });
                                        }}
                                        placeholder="e.g., Thank you for your interest"
                                        className="w-full px-3 py-2 rounded-lg bg-background border text-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Email Body (Plain Text)</label>
                                    <textarea
                                        value={form.actions[0].params.body || ''}
                                        onChange={e => {
                                            const newActions = [...form.actions];
                                            newActions[0].params = { ...newActions[0].params, body: e.target.value };
                                            setForm({ ...form, actions: newActions });
                                        }}
                                        rows={4}
                                        placeholder="Type your legacy auto-reply..."
                                        className="w-full px-3 py-2 rounded-lg bg-background border text-sm resize-none"
                                    />
                                    <p className="text-[10px] text-muted-foreground italic">Legacy replies do not support dynamic variables or HTML formatting. Use Visual Flows for rich authoring.</p>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center gap-2 py-2">
                            <input
                                type="checkbox"
                                id="active-toggle"
                                checked={form.isActive}
                                onChange={e => setForm({ ...form, isActive: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label htmlFor="active-toggle" className="text-sm font-medium">Enable this rule</label>
                        </div>

                        <SheetFooter>
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                            <Button type="submit">{isEditing ? 'Update Rule' : 'Create Rule'}</Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            {/* Create Visual Flow Modal */}
            <Sheet open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <SheetContent side="right" className="sm:max-w-[440px]">
                    <SheetHeader>
                        <SheetTitle>Create Visual Flow</SheetTitle>
                        <SheetDescription>
                            Start a modern inbox automation using the visual builder.
                        </SheetDescription>
                    </SheetHeader>
                    <form onSubmit={submitCreateFlow} className="space-y-6 pt-4">
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Automation Name</label>
                            <input
                                autoFocus
                                required
                                value={newFlowName}
                                onChange={e => setNewFlowName(e.target.value)}
                                placeholder="e.g., Interested Lead Follow-up"
                                className="w-full px-4 py-3 rounded-xl bg-background border border-border/50 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-sm font-medium"
                            />
                            {!newFlowName.trim() && (
                                <p className="text-[10px] text-muted-foreground italic">Required</p>
                            )}
                        </div>
                        <SheetFooter className="gap-2">
                            <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                            <Button
                                type="submit"
                                disabled={!newFlowName.trim() || isCreating}
                                className="gap-2 font-bold min-w-[100px]"
                            >
                                {isCreating ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 animate-spin" /> Creating...
                                    </>
                                ) : (
                                    'Create Flow'
                                )}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Modal */}
            <Sheet open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <SheetContent side="right" className="sm:max-w-[400px]">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2 text-destructive">
                            <ShieldAlert className="h-5 w-5" /> Delete Automation?
                        </SheetTitle>
                        <SheetDescription>
                            Are you sure you want to delete this automation? This action cannot be undone and will stop any active processes associated with it.
                        </SheetDescription>
                    </SheetHeader>
                    <SheetFooter className="gap-2 pt-4">
                        <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</Button>
                        <Button
                            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground font-bold gap-2"
                            onClick={submitDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Delete Permanently'}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
}
